import React, { useState, useEffect } from 'react';
import { get } from '../../services/apiController';
import { toast, ToastContainer } from 'react-toastify';
import { DEBOUNCE_DELAY, monthsDropdown } from '../../util/constant';
import { Table } from 'react-bootstrap';
import Select from 'react-select';
import useDebounce from '../useDebounce';
import FactoryDropdown from '../vm/FactoryDropdown';
import { saveAs } from 'file-saver';
import { GrDocumentPdf } from 'react-icons/gr';

const years = Array.from({ length: 4 }, (_, i) => {
  const year = 2024 + i;
  return { value: year, label: year.toString() };
});

const MachineExpenseReport = () => {
  const [expenses, setExpenses] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sections, setSections] = useState([]);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    section: ''
  });

  // Fetch sections
  useEffect(() => {
    get('/mm/section')
      .then((response) => {
        const sectionOptions = response?.data?.map(section => ({
          value: section?.value,
          label: section?.value
        })) || [];
        setSections(sectionOptions);
      })
      .catch((err) => {
        console.error('Error fetching sections:', err);
        toast.error('Error fetching sections');
      });
  }, []);
  const fetchExpenses = (downloadExcel) => {
    const { month, year, section } = filters;
    if (!month || !year) {
      toast.error('Month and year required.');
      return;
    }
    let url = `/mm/diesel-log-entries/report?month=${month}&year=${year}`;
    if (section) url += `&section=${section}`;
    if (downloadExcel) url += `&downloadExcel=true`;
    get(url, { responseType: downloadExcel ? 'arraybuffer' : 'json' })
      .then((response) => {
        if (downloadExcel) {
          const { month, year } = filters;
          const fileName = `Machine Management Expense Report - ${monthsDropdown.find((m) => m.value === month)?.label} ${year}.pdf`;
          const blob = new Blob([response], { type: 'application/pdf' });
          saveAs(blob, fileName);
        } else {
          setExpenses(response?.data || []);
          setTotalRecords(response?.data?.length || 0);
        }
      })
      .catch((err) => {
        console.log('err', err);
        toast.error(err?.response?.data?.message || err.message);
      });
  };

  useDebounce(
    () => {
      fetchExpenses();
    },
    [filters],
    DEBOUNCE_DELAY
  );
  // Calculate totals
  const totalMaterialCost = expenses.reduce((total, machine) => {
    const breakdownCosts = machine.breakdowns?.reduce((sum, breakdown) => {
      const sparesTotal = breakdown.spares?.reduce((spareSum, spare) =>
        spareSum + (Number(spare.price) || 0), 0) || 0;
      return sum + sparesTotal;
    }, 0) || 0;
    const serviceCosts = machine.services?.reduce((sum, service) =>
      sum + (Number(service.materialCost) || 0), 0) || 0;
    return total + breakdownCosts + serviceCosts;
  }, 0);

  const totalLabourCost = expenses.reduce((total, machine) => {
    const breakdownCosts = machine.breakdowns?.reduce((sum, breakdown) => {
      const sparesLabourTotal = breakdown.spares?.reduce((spareSum, spare) =>
        spareSum + (Number(spare.labourCost) || 0), 0) || 0;
      return sum + sparesLabourTotal;
    }, 0) || 0;
    const serviceCosts = machine.services?.reduce((sum, service) =>
      sum + (Number(service.serviceCost) || 0), 0) || 0;
    return total + breakdownCosts + serviceCosts;
  }, 0);

  return (
    <div>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Machine Expense Report | {totalRecords ?? 0}
          </h4>

          <div className="d-flex align-items-center gap-3">
            <div className="form-floating text-start">
              <Select
                className="input"
                value={monthsDropdown.find((m) => m.value === filters.month)}
                options={monthsDropdown}
                onChange={(selectedOption) => setFilters((prev) => ({ ...prev, month: selectedOption?.value }))}
                placeholder="Select Month"
                isClearable
                isSearchable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '55px',
                    minWidth: '200px',
                  }),
                }}
              />
            </div>
            <div className="form-floating text-start">
              <Select
                className="input"
                value={years.find((y) => y.value === filters.year)}
                options={years}
                onChange={(selectedOption) => setFilters((prev) => ({ ...prev, year: selectedOption?.value }))}
                placeholder="Select Year"
                isClearable
                isSearchable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '55px',
                    minWidth: '200px',
                  }),
                }}
              />
            </div>            <FactoryDropdown
              withFloatingLable={true}
              onFactoryChange={() => {
                fetchExpenses();
              }}
            />
            <div className="form-floating text-start">
              <Select
                className="input"
                value={sections.find(s => s.value === filters.section)}
                options={sections}
                onChange={(selectedOption) => setFilters(prev => ({ ...prev, section: selectedOption?.value }))}
                placeholder="Select Section"
                isClearable
                isSearchable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '55px',
                    minWidth: '200px',
                  }),
                }}
              />
            </div>
            <GrDocumentPdf
              style={{ cursor: 'pointer', width: '2rem', height: '2rem' }}
              onClick={() => fetchExpenses(true)}
              className="react-icons red"
              title="download pdf"
            />
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <Table responsive striped className="align-middle">
            <thead>
              <tr className="text-center">
                <th className="align-middle" style={{ width: '60px' }}>S.No.</th>
                <th className="align-middle">Machine Name</th>
                <th className="align-middle">Item Number</th>
                <th className="align-middle">Factory</th>
                <th className="align-middle" style={{ width: '120px' }}>Starting Reading</th>
                <th className="align-middle" style={{ width: '120px' }}>Ending Reading</th>
                <th className="align-middle" style={{ width: '100px' }}>Total Hours</th>
                <th className="align-middle" style={{ width: '100px' }}>Total KWH</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {expenses?.map((machine, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className="text-start">{machine.machineName || '-'}</td>
                  <td>{machine.itemNumber || '-'}</td>
                  <td>{machine.factoryName || '-'}</td>
                  <td>{machine.startingReading ? Number(machine.startingReading).toFixed(2) : '-'}</td>
                  <td>{machine.endingReading ? Number(machine.endingReading).toFixed(2) : '-'}</td>
                  <td>{machine.totalHours ? Number(machine.totalHours).toFixed(2) : '-'}</td>
                  <td>{machine.totalKWH ? Number(machine.totalKWH).toFixed(2) : '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f3f6f9', fontWeight: '500' }}>
                <td className="text-center" colSpan="8">
                  Total:
                </td>
              </tr>
            </tfoot>
          </Table>

          {/* Expense Details Sections */}
          {expenses?.map((machine, index) => (
            (machine.breakdowns?.length > 0 || machine.services?.length > 0) && (
              <div key={`expense-${index}`} className="mt-4 px-3">
                <h5 className="mb-3 fw-bold" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem' }}>
                  Expense Details - {machine.machineName}
                </h5>

                {/* Breakdowns Section */}
                {machine.breakdowns?.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3 fw-bold">Breakdowns</h6>
                    <Table responsive striped size="sm" className="align-middle">
                      <thead>
                        <tr className="text-center">
                          <th style={{ width: '120px' }}>Date</th>
                          <th style={{ width: '80px' }}>Section</th>
                          <th style={{ width: '80px' }}>AMC</th>
                          <th style={{ width: '80px' }}>Serviced Date</th>
                          <th style={{ width: '80px' }}>Spare Name</th>
                          <th style={{ width: '80px' }}>Numbers</th>
                          <th style={{ width: '120px' }}>Material Cost</th>
                          <th style={{ width: '120px' }}>Labour Cost</th>
                          <th style={{ width: '120px' }}>Total Cost</th>
                          <th>Supervised By</th>
                        </tr>
                      </thead>                      <tbody className="text-center">
                        {machine.breakdowns.map((breakdown, idx) => (
                          breakdown.spares.map((spare, spareIdx) => (
                            <tr key={`${idx}-${spareIdx}`}>
                              <td>{spareIdx === 0 ? new Date(breakdown.breakDate).toLocaleDateString() : ''}</td>
                              <td>{spareIdx === 0 ? breakdown.section || '-' : ''}</td>
                              <td>{spareIdx === 0 ? breakdown.isAmc ? 'Yes' : 'No' : ''}</td>
                              <td>{spareIdx === 0 ? (breakdown.servicedDate ? new Date(breakdown.servicedDate).toLocaleDateString() : '-') : ''}</td>
                              <td>{spare.spareName || '-'}</td>
                              <td>{spare.numbers || '-'}</td>
                              <td>₹{(Number(spare.price) || 0).toFixed(2)}</td>
                              <td>₹{(Number(spare.labourCost) || 0).toFixed(2)}</td>
                              <td>₹{(Number(spare.totalAmount) || 0).toFixed(2)}</td>
                              <td>{spareIdx === 0 ? breakdown.supervisedBy || '-' : ''}</td>
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Services Section */}
                {machine.services?.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-3 fw-bold">Services</h6>
                    <Table responsive striped size="sm" className="align-middle">
                      <thead>
                        <tr className="text-center">
                          <th style={{ width: '120px' }}>Date</th>
                          <th style={{ width: '80px' }}>AMC</th>
                          <th style={{ width: '120px' }}>Material Cost</th>
                          <th style={{ width: '120px' }}>Labour Cost</th>
                          <th style={{ width: '120px' }}>Total Cost</th>
                          <th>Supervised By</th>
                        </tr>
                      </thead>
                      <tbody className="text-center">
                        {machine.services.map((service, idx) => (
                          <tr key={idx}>
                            <td>{new Date(service.servicedDate).toLocaleDateString()}</td>
                            <td>{service.isAmc ? 'Yes' : 'No'}</td>
                            <td>₹{(Number(service.materialCost) || 0).toFixed(2)}</td>
                            <td>₹{(Number(service.serviceCost) || 0).toFixed(2)}</td>
                            <td>₹{((Number(service.materialCost) || 0) + (Number(service.serviceCost) || 0)).toFixed(2)}</td>
                            <td>{service.supervisedBy || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )
          ))}

          {/* Grand Totals Section */}
          <div className="mt-4 px-3">
            <h5 className="mb-3 fw-bold" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem' }}>
              Grand Totals
            </h5>
            <Table responsive striped size="sm" className="align-middle" style={{ maxWidth: '600px' }}>
              <thead>
                <tr className="text-center">
                  <th>Total Material Cost</th>
                  <th>Total Labour Cost</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody className="text-center">
                <tr>
                  <td>₹{(Number(totalMaterialCost) || 0).toFixed(2)}</td>
                  <td>₹{(Number(totalLabourCost) || 0).toFixed(2)}</td>
                  <td>₹{((Number(totalMaterialCost) || 0) + (Number(totalLabourCost) || 0)).toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineExpenseReport;
